import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../constants';

interface LiveSession {
  id: number;
  orderId: number;
  orderNumber?: string;
  technicianName?: string;
  makeName?: string;
  modelName?: string;
  serviceSummary?: string;
  youtubeUrl: string;
  youtubeVideoId?: string;
  title?: string;
  startedAt?: string;
}

const LiveRepairsSection: React.FC = () => {
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSessions = () => {
    fetch(`${API_BASE_URL}/LiveRepair/active`)
      .then(r => r.json())
      .then(data => {
        if (data.isSuccess && Array.isArray(data.entity)) {
          setSessions(data.entity.filter((s: LiveSession) => s.youtubeVideoId));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSessions();
    const t = setInterval(fetchSessions, 60000); // refresh every 60s
    return () => clearInterval(t);
  }, []);

  if (loading || sessions.length === 0) return null;

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <span className="flex items-center gap-1.5">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
          </span>
        </span>
        <h2 className="text-base md:text-lg font-semibold text-gray-900">Live Repairs Happening Now</h2>
        <span className="text-xs font-semibold text-red-500 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
          {sessions.length} LIVE
        </span>
      </div>

      {/* Scrollable row */}
      <div className="flex gap-3 overflow-x-auto pb-2 px-1" style={{ scrollbarWidth: 'none' }}>
        {sessions.map(session => (
          <div
            key={session.id}
            className="flex-shrink-0 w-72 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
          >
            {/* YouTube embed */}
            <div className="relative" style={{ aspectRatio: '16/9', background: '#000' }}>
              <iframe
                src={`https://www.youtube.com/embed/${session.youtubeVideoId}?autoplay=0&mute=1`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                title={session.title || 'Live Repair'}
                style={{ border: 'none' }}
              />
              {/* LIVE badge overlay */}
              <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow">
                <span className="w-1.5 h-1.5 rounded-full bg-white inline-block animate-pulse" />
                LIVE
              </div>
            </div>

            {/* Info */}
            <div className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-tools text-red-500 text-xs" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-bold text-gray-800 truncate">
                    {session.makeName} {session.modelName}
                  </div>
                  {session.serviceSummary && (
                    <div className="text-xs text-minto-600 truncate">{session.serviceSummary}</div>
                  )}
                </div>
              </div>
              {session.technicianName && (
                <div className="text-xs text-gray-500 truncate">
                  by <span className="font-medium text-gray-700">{session.technicianName}</span>
                </div>
              )}
              <a
                href={session.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-1.5 w-full text-xs font-semibold text-red-600 border border-red-200 rounded-lg py-1.5 hover:bg-red-50 transition-colors"
              >
                <i className="fab fa-youtube" />
                Watch on YouTube
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LiveRepairsSection;
